/**
 * Connection State Management Service
 * 
 * Provides reliable backend availability detection with:
 * - Connection states: CONNECTED, CONNECTING, DISCONNECTED, RECOVERING
 * - Exponential backoff retry for transient failures
 * - Event-based state change notifications
 * - Prevents infinite request loops when backend is unavailable
 */

import axios from "axios";
import { apiUrl } from "./apiConfig";

export type ConnectionState = 
  | "CONNECTED" 
  | "CONNECTING" 
  | "DISCONNECTED" 
  | "RECOVERING";

interface ConnectionStatus {
  state: ConnectionState;
  lastCheck: Date | null;
  consecutiveFailures: number;
  lastError: string | null;
}

type ConnectionListener = (status: ConnectionStatus) => void;

const HEALTH_ENDPOINT = apiUrl("/api/health");
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
const MAX_CONSECUTIVE_FAILURES = 5;
const HEALTH_CHECK_TIMEOUT_MS = 5000;

let currentStatus: ConnectionStatus = {
  state: "CONNECTING",
  lastCheck: null,
  consecutiveFailures: 0,
  lastError: null,
};

const listeners = new Set<ConnectionListener>();
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let isCheckInProgress = false;

/**
 * Notify all registered listeners of state change.
 */
function notifyListeners(): void {
  listeners.forEach((listener) => {
    try {
      listener({ ...currentStatus });
    } catch {
      // Listener errors should not break the service
    }
  });
}

/**
 * Update connection state and notify listeners if changed.
 */
function updateState(newState: ConnectionState, error?: string): void {
  const oldState = currentStatus.state;
  currentStatus = {
    ...currentStatus,
    state: newState,
    lastCheck: new Date(),
    lastError: error ?? null,
  };
  
  if (oldState !== newState) {
    notifyListeners();
  }
}

/**
 * Calculate exponential backoff delay.
 */
function getRetryDelay(): number {
  const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, currentStatus.consecutiveFailures);
  return Math.min(delay, MAX_RETRY_DELAY_MS);
}

/**
 * Perform a health check against the backend.
 * Returns true if backend is available, false otherwise.
 */
async function performHealthCheck(): Promise<boolean> {
  if (isCheckInProgress) return currentStatus.state === "CONNECTED";
  
  isCheckInProgress = true;
  
  try {
    const response = await axios.get(HEALTH_ENDPOINT, {
      timeout: HEALTH_CHECK_TIMEOUT_MS,
      validateStatus: (status) => status === 200,
    });
    
    if (response.data?.status === "ok") {
      currentStatus.consecutiveFailures = 0;
      updateState("CONNECTED");
      return true;
    }
    
    throw new Error("Unexpected health response");
  } catch (error) {
    currentStatus.consecutiveFailures++;
    
    const errorMessage = axios.isAxiosError(error)
      ? error.code === "ECONNREFUSED" || error.code === "ERR_NETWORK"
        ? "Backend unavailable"
        : error.message
      : "Health check failed";
    
    if (currentStatus.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      updateState("DISCONNECTED", errorMessage);
    } else {
      updateState("RECOVERING", errorMessage);
    }
    
    return false;
  } finally {
    isCheckInProgress = false;
  }
}

/**
 * Schedule a retry with exponential backoff.
 */
function scheduleRetry(): void {
  if (retryTimeoutId) {
    clearTimeout(retryTimeoutId);
  }
  
  if (currentStatus.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    // Stop retrying after max failures - user must manually trigger recovery
    return;
  }
  
  const delay = getRetryDelay();
  retryTimeoutId = setTimeout(async () => {
    await performHealthCheck();
    if (currentStatus.state !== "CONNECTED") {
      scheduleRetry();
    }
  }, delay);
}

/**
 * Check if the backend is currently connected.
 */
export function isConnected(): boolean {
  return currentStatus.state === "CONNECTED";
}

/**
 * Get current connection status.
 */
export function getConnectionStatus(): ConnectionStatus {
  return { ...currentStatus };
}

/**
 * Register a listener for connection state changes.
 * Returns an unsubscribe function.
 */
export function onConnectionChange(listener: ConnectionListener): () => void {
  listeners.add(listener);
  // Immediately notify with current state
  listener({ ...currentStatus });
  
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Initialize connection monitoring.
 * Call this once at app startup.
 */
export async function initConnectionMonitor(): Promise<boolean> {
  const isHealthy = await performHealthCheck();
  
  if (!isHealthy) {
    scheduleRetry();
  }
  
  return isHealthy;
}

/**
 * Manually trigger a connection recovery attempt.
 * Use this when user clicks "Retry" button.
 */
export async function attemptRecovery(): Promise<boolean> {
  currentStatus.consecutiveFailures = 0;
  updateState("RECOVERING");
  
  const isHealthy = await performHealthCheck();
  
  if (!isHealthy) {
    scheduleRetry();
  }
  
  return isHealthy;
}

/**
 * Check if an error indicates backend unavailability.
 * Use this to distinguish network errors from API errors.
 */
export function isConnectionError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  
  // No response at all = network/connection error
  if (!error.response) {
    return true;
  }
  
  // Connection-related error codes
  const connectionErrorCodes = [
    "ECONNREFUSED",
    "ECONNRESET", 
    "ENOTFOUND",
    "ETIMEDOUT",
    "ERR_NETWORK",
    "ERR_CONNECTION_REFUSED",
  ];
  
  return connectionErrorCodes.includes(error.code ?? "");
}

/**
 * Mark connection as failed (called by API interceptors on connection errors).
 */
export function markConnectionFailed(error?: string): void {
  currentStatus.consecutiveFailures++;
  
  if (currentStatus.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    updateState("DISCONNECTED", error);
  } else if (currentStatus.state === "CONNECTED") {
    updateState("RECOVERING", error);
    scheduleRetry();
  }
}

/**
 * Mark connection as successful (called after successful API requests).
 */
export function markConnectionSuccess(): void {
  if (currentStatus.state !== "CONNECTED") {
    currentStatus.consecutiveFailures = 0;
    updateState("CONNECTED");
  }
}

/**
 * Stop all retry timers (for cleanup on unmount).
 */
export function stopConnectionMonitor(): void {
  if (retryTimeoutId) {
    clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
  }
  listeners.clear();
}
